'use client';

import { BOX_SYSTEMS, getBoxSystem } from '../../config/boxPatterns';
import {
  // getScaleNotes,
  COMMON_PROGRESSIONS,
  MODES,
  SCALE_DEGREES,
  getIntervalName,
} from '../../config/musicTheory';

export default function ScaleInfo({ root, mode, box, boxModeEnabled }) {
  const modeInfo = MODES[mode];
  if (!modeInfo) {
    console.error(`Invalid mode: ${mode}`);
    return <div>Invalid scale type</div>;
  }

  const { name, description, intervals, usage } = modeInfo;
  // const scaleNotes = getScaleNotes(root, mode);
  const boxSystem = getBoxSystem(mode);

  return (
    <div className="space-y-6 m-4">
      {/* Scale Notes */}
      {/* <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h2 className="card-title text-lg">
            Scale Notes
          </h2>
          <div className="flex flex-wrap gap-2">
            {scaleNotes.map((note, index) => (
              <div
                key={`${note}-${index}`}
                className={`badge badge-lg ${
                  note === root ? 'badge-primary' : 'badge-ghost'
                }`}
                title={SCALE_DEGREES[intervals[index]]?.function}
              >
                <span className="font-medium">{note}</span>
                <span className="text-xs ml-1 opacity-70">
                    ({intervals[index]})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* Scale Construction */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h3 className="card-title text-lg">
              {root} {name}
            </h3>
            <p>{description}</p>

            {/* Interval List */}
            <div className="my-3">
              <div className="text-sm font-medium mb-1">Intervals:</div>
              <div className="flex flex-wrap gap-2">
                {intervals.map((interval) => {
                  const degreeInfo =
                    SCALE_DEGREES[interval.replaceAll(/[#♭]/g, '')] || {};
                  const intervalName = getIntervalName(interval);
                  const tooltipText = degreeInfo.name
                    ? `${degreeInfo.name} - ${degreeInfo.function || ''}`
                    : '';

                  return (
                    <div
                      key={interval}
                      className="tooltip tooltip-bottom"
                      data-tip={tooltipText}
                    >
                      <div className="badge badge-ghost">
                        <span className="font-mono">{interval}</span>
                        {intervalName && (
                          <span className="ml-1 text-xs opacity-70">
                            {intervalName}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {usage && (
              <p className="text-base-content/70 text-sm mt-3">
                Common Usage: {usage}
              </p>
            )}
          </div>
        </div>

        {/* Box Pattern Info */}
        {boxSystem && boxModeEnabled && (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg">Pattern System</h3>
              <div className="space-y-2">
                <p>
                  {box === 'all' ? (
                    'Showing the complete scale across the fretboard'
                  ) : (
                    <>
                      Currently viewing{' '}
                      {boxSystem === BOX_SYSTEMS.PENTATONIC
                        ? `Box ${box}`
                        : (boxSystem === BOX_SYSTEMS.CAGED
                          ? `${box} Shape`
                          : `Pattern ${box}`)}
                    </>
                  )}
                </p>
                <div className="text-sm text-base-content/70">
                  {boxSystem === BOX_SYSTEMS.PENTATONIC
                    ? 'The pentatonic box system divides the scale into 5 interconnected patterns'
                    : (boxSystem === BOX_SYSTEMS.CAGED
                      ? 'The CAGED system is based on open chord shapes moved up the neck'
                      : 'Three-Note-Per-String patterns are ideal for fluid legato playing')}
                </div>
              </div>
            </div>
          </div>
        )}
        {boxSystem && !boxModeEnabled && (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg">Full Scale Mode</h3>
              <div className="space-y-2">
                <p>
                  Currently viewing {root} {name}
                </p>
                <div className="text-sm text-base-content/70">
                  Enable box mode to view and practice boxes/patterns
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Common Progressions */}
        {(COMMON_PROGRESSIONS[mode] ||
          COMMON_PROGRESSIONS[mode.toLowerCase()]) && (
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg">Common Progressions</h3>
              <div className="flex flex-wrap gap-3">
                {(
                  COMMON_PROGRESSIONS[mode] ||
                  COMMON_PROGRESSIONS[mode.toLowerCase()] ||
                  []
                ).map((progression) => (
                  <div
                    key={progression}
                    className="badge badge-lg badge-outline"
                  >
                    {progression}
                  </div>
                ))}
              </div>
              <p className="text-sm text-base-content/70 mt-2">
                Practice these progressions to internalize the sound of the
                scale
              </p>
            </div>
          </div>
        )}

        {/* Practice Tips */}
        <div className="card bg-info/10 shadow-sm">
          <div className="card-body">
            <h3 className="card-title text-lg">Practice Tips</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Start slowly and focus on clean note articulation</li>
              <li>Practice ascending and descending patterns</li>
              <li>Use a metronome to develop timing and speed</li>
              <li>Focus on the relationship between scale degrees</li>
              {boxSystem === BOX_SYSTEMS.PENTATONIC && (
                <li>Connect adjacent box patterns to cover more of the neck</li>
              )}
              {boxSystem === BOX_SYSTEMS.CAGED && (
                <li>Practice transitioning between adjacent CAGED shapes</li>
              )}
              {boxSystem === BOX_SYSTEMS.TNPS && (
                <li>Focus on consistent finger placement across all strings</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
